// دیالوگ‌های SCR-STAFF-400 (FLOW-04): افزودن خدمت (Ins؛ هشدار محدودیت با دلیل — C-AGENT-532)، ویرایش تعداد و ارائه‌دهنده، تعیین مبلغ Range
// (US-BIL-012)، تخفیف دستی قلم یا صورتحساب با سقف و تأیید مدیر (US-BIL-011؛ useApproval) و پرداخت‌کننده (US-BIL-003).
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import {
  isApiError,
  unwrap,
  type BilManualDiscountKind,
  type BilPayerView,
  type BilServiceLookupItem,
  type BilVisitLineView,
  type BilVisitView,
  type PatientSearchItem,
} from '@phoenix/api-client';
import { useIdempotencyKey, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { faDigits, FormDialog, MoneyFieldRHF, useMoneyFormatter } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useApproval } from '../iam/useApproval';
import { PatientPicker } from '../patients/PatientPicker';
import { bilKeys, num, rial, SCR_STAFF_400 } from './common';

/** به‌روزرسانی Cache مراجعه با پاسخ هر عملیات (VisitView کامل). */
export function useVisitCache(visitId: string) {
  const { session } = useTenantSession();
  const queryClient = useQueryClient();
  return (view: BilVisitView) => {
    queryClient.setQueryData(bilKeys.visit(session?.tenantId, visitId), view);
    void queryClient.invalidateQueries({ queryKey: ['billing', session?.tenantId, 'visits'] });
  };
}

function useDebounced(value: string, delay = 250): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

export function AddLineDialog({ visit, onClose }: { visit: BilVisitView; onClose: () => void }) {
  const { t } = useTranslation('bil');
  const { tenantApi, session } = useTenantSession();
  const update = useVisitCache(visit.id);
  const idempotency = useIdempotencyKey();
  const [text, setText] = useState('');
  const q = useDebounced(text);
  const [service, setService] = useState<BilServiceLookupItem | null>(null);
  const [quantity, setQuantity] = useState('1');
  const [providerId, setProviderId] = useState('');
  const [planned, setPlanned] = useState(false);
  const [restriction, setRestriction] = useState<string | null>(null);
  const [restrictionReason, setRestrictionReason] = useState('');
  const lookups = useQuery({
    queryKey: bilKeys.lookups(session?.tenantId, visit.id, q),
    queryFn: () =>
      unwrap(tenantApi.GET('/api/v1/billing/visits/{id}/lookups', { params: { path: { id: visit.id }, query: { q: q || undefined } } })),
  });
  const add = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/billing/visits/{id}/lines', {
          params: { path: { id: visit.id } },
          body: {
            serviceId: service?.id ?? '',
            quantity: Number(quantity) || 1,
            providerId: providerId || null,
            planned,
            amount: null,
            amountReason: null,
            restrictionReason: restriction ? restrictionReason.trim() || null : null,
          },
          headers: { 'Idempotency-Key': idempotency.current() },
        }),
      ),
    onSettled: (_data, error) => idempotency.settle(error ?? undefined),
    onSuccess: (view) => {
      update(view);
      onClose();
    },
    onError: (error) => {
      // PHX-BIL-0207: محدودیت خدمت (سن، جنسیت، فاصله) ← هشدار و دلیل الزامی برای ادامه.
      if (isApiError(error) && error.code === 'PHX-BIL-0207') {
        setRestriction(String(error.extensions.codes ?? ''));
      }
    },
  });
  const restrictionPending = restriction !== null && restrictionReason.trim().length < 3;
  return (
    <FormDialog
      open
      titleKey="bil:lines.add"
      screenId={SCR_STAFF_400}
      onSubmit={() => service && !restrictionPending && add.mutate()}
      onClose={onClose}
      isDirty={service !== null}
      isSubmitting={add.isPending}
      submitError={restriction !== null ? undefined : add.error}
      submitKey="bil:lines.add_submit"
    >
      <Autocomplete<BilServiceLookupItem>
        value={service}
        onChange={(_event, next) => {
          setService(next);
          setRestriction(null);
        }}
        inputValue={text}
        onInputChange={(_event, next) => setText(next)}
        options={lookups.data?.services ?? []}
        loading={lookups.isFetching}
        filterOptions={(options) => options}
        getOptionLabel={(option) => `${option.name} (${option.code})`}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        noOptionsText={t('bil:lines.no_services')}
        renderInput={(params) => (
          <TextField
            {...params}
            autoFocus
            required
            label={t('bil:lines.service')}
            slotProps={{ htmlInput: { ...params.inputProps, 'data-testid': 'bil-line-service' } }}
          />
        )}
      />
      <TextField
        type="number"
        label={t('bil:lines.quantity')}
        value={quantity}
        onChange={(event) => setQuantity(event.target.value)}
        slotProps={{ htmlInput: { min: 1, 'data-testid': 'bil-line-quantity' } }}
      />
      <TextField
        select
        label={t('bil:lines.provider')}
        value={providerId}
        onChange={(event) => setProviderId(event.target.value)}
        slotProps={{ htmlInput: { 'data-testid': 'bil-line-provider' } }}
      >
        <MenuItem value="">{t('bil:lines.no_provider')}</MenuItem>
        {(lookups.data?.providers ?? []).map((provider) => (
          <MenuItem key={provider.id} value={provider.id}>
            {provider.name}
          </MenuItem>
        ))}
      </TextField>
      <FormControlLabel
        control={<Switch checked={planned} onChange={(event) => setPlanned(event.target.checked)} />}
        label={t('bil:lines.planned')}
      />
      {restriction !== null && (
        <>
          <Alert severity="warning" data-testid="bil-line-restriction">
            {t('bil:lines.restriction', { codes: restriction })}
          </Alert>
          <TextField
            required
            label={t('bil:lines.restriction_reason')}
            value={restrictionReason}
            onChange={(event) => setRestrictionReason(event.target.value)}
            slotProps={{ htmlInput: { maxLength: 500, 'data-testid': 'bil-line-restriction-reason' } }}
          />
        </>
      )}
    </FormDialog>
  );
}

export function EditLineDialog({ visit, line, onClose }: { visit: BilVisitView; line: BilVisitLineView; onClose: () => void }) {
  const { t } = useTranslation('bil');
  const { tenantApi, session } = useTenantSession();
  const update = useVisitCache(visit.id);
  const [quantity, setQuantity] = useState(String(num(line.quantity)));
  const [providerId, setProviderId] = useState(line.providerId ?? '');
  const lookups = useQuery({
    queryKey: bilKeys.lookups(session?.tenantId, visit.id, ''),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/billing/visits/{id}/lookups', { params: { path: { id: visit.id }, query: {} } })),
  });
  const save = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.PUT('/api/v1/billing/visits/{id}/lines/{lineId}', {
          params: { path: { id: visit.id, lineId: line.id } },
          body: { quantity: Number(quantity) || 1, providerId: providerId || null, payer: line.payer ?? null },
        }),
      ),
    onSuccess: (view) => {
      update(view);
      onClose();
    },
  });
  return (
    <FormDialog open titleKey="bil:lines.edit" screenId={SCR_STAFF_400} onSubmit={() => save.mutate()} onClose={onClose} isDirty isSubmitting={save.isPending} submitError={save.error}>
      <Typography variant="body2">{line.title}</Typography>
      <TextField
        type="number"
        label={t('bil:lines.quantity')}
        value={quantity}
        onChange={(event) => setQuantity(event.target.value)}
        slotProps={{ htmlInput: { min: 1, 'data-testid': 'bil-edit-quantity' } }}
      />
      <TextField select label={t('bil:lines.provider')} value={providerId} onChange={(event) => setProviderId(event.target.value)}>
        <MenuItem value="">{t('bil:lines.no_provider')}</MenuItem>
        {line.providerId && !(lookups.data?.providers ?? []).some((provider) => provider.id === line.providerId) ? (
          <MenuItem value={line.providerId}>{line.providerName ?? line.providerId}</MenuItem>
        ) : null}
        {(lookups.data?.providers ?? []).map((provider) => (
          <MenuItem key={provider.id} value={provider.id}>
            {provider.name}
          </MenuItem>
        ))}
      </TextField>
    </FormDialog>
  );
}

interface AmountForm {
  amount: number | null;
}

/** مبلغ قلم Range در بازه (BR-CAT-022)؛ بیرون از بازه با دلیل و cat.price.manage. */
export function AmountDialog({ visit, line, onClose }: { visit: BilVisitView; line: BilVisitLineView; onClose: () => void }) {
  const { t } = useTranslation('bil');
  const { tenantApi } = useTenantSession();
  const format = useMoneyFormatter();
  const update = useVisitCache(visit.id);
  const [reason, setReason] = useState('');
  const { control, watch } = useForm<AmountForm>({ defaultValues: { amount: line.manualUnitAmount?.amount ?? null } });
  const amount = watch('amount');
  const min = line.rangeMin?.amount;
  const max = line.rangeMax?.amount;
  const outOfRange = amount !== null && ((min !== undefined && amount < min) || (max !== undefined && amount > max));
  const save = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.PUT('/api/v1/billing/visits/{id}/lines/{lineId}/amount', {
          params: { path: { id: visit.id, lineId: line.id } },
          body: { amount: rial(amount ?? 0), reason: reason.trim() || null },
        }),
      ),
    onSuccess: (view) => {
      update(view);
      onClose();
    },
  });
  return (
    <FormDialog
      open
      titleKey="bil:amount.title"
      screenId={SCR_STAFF_400}
      onSubmit={() => amount !== null && save.mutate()}
      onClose={onClose}
      isDirty={amount !== null}
      isSubmitting={save.isPending}
      submitError={save.error}
    >
      <Typography variant="body2">{line.title}</Typography>
      {min !== undefined && max !== undefined && (
        <Alert severity="info" data-testid="bil-amount-range">
          {t('bil:amount.range', { min: format(min, 'IRR'), max: format(max, 'IRR') })}
        </Alert>
      )}
      <div data-testid="bil-amount-input">
        <MoneyFieldRHF name="amount" control={control} labelKey="bil:amount.amount" required fullWidth autoFocus />
      </div>
      {outOfRange && (
        <TextField
          required
          label={t('bil:amount.reason')}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          helperText={t('bil:amount.out_of_range')}
          slotProps={{ htmlInput: { maxLength: 500, 'data-testid': 'bil-amount-reason' } }}
        />
      )}
    </FormDialog>
  );
}

interface DiscountForm {
  amount: number | null;
}

/**
 * تخفیف دستی (US-BIL-011): درصد یا مبلغ، دلیل الزامی؛ approvals:check با کلید سقف درصد یا مبلغ ← در سقف بی‌درنگ، بالاتر ApprovalDialog (مدیر حاضر
 * یا درخواست). approval_id همراه درخواست می‌رود و سرور مصرف می‌کند (C-AGENT-537).
 */
export function DiscountDialog({ visit, line, onClose }: { visit: BilVisitView; line: BilVisitLineView | null; onClose: () => void }) {
  const { t } = useTranslation('bil');
  const { tenantApi } = useTenantSession();
  const update = useVisitCache(visit.id);
  const { requireApproval, dialog } = useApproval();
  const current = line ? line.manualDiscount : visit.invoiceDiscount;
  const [kind, setKind] = useState<BilManualDiscountKind>(current?.kind ?? 'Percent');
  const [percent, setPercent] = useState(current?.kind === 'Percent' ? String(num(current.value)) : '');
  const [reason, setReason] = useState(current?.reason ?? '');
  const [pendingApproval, setPendingApproval] = useState<string | null>(null);
  const [error, setError] = useState<unknown>();
  const [busy, setBusy] = useState(false);
  const { control, watch } = useForm<DiscountForm>({ defaultValues: { amount: current?.kind === 'Amount' ? num(current.value) : null } });
  const amount = watch('amount');
  const value = kind === 'Percent' ? Number(percent) || 0 : (amount ?? 0);
  const valid = reason.trim().length >= 3 && (kind === 'Percent' ? value >= 1 && value <= 100 : value > 0);

  const send = (approvalId: string | null) => {
    const body = { kind, value, reason: reason.trim(), approvalId };
    return line
      ? unwrap(tenantApi.PUT('/api/v1/billing/visits/{id}/lines/{lineId}/manual-discount', { params: { path: { id: visit.id, lineId: line.id } }, body }))
      : unwrap(tenantApi.PUT('/api/v1/billing/visits/{id}/manual-discount', { params: { path: { id: visit.id } }, body }));
  };

  const submit = async () => {
    if (!valid || busy) {
      return;
    }
    setBusy(true);
    setError(undefined);
    try {
      let approvalId = pendingApproval;
      if (!approvalId) {
        const outcome = await requireApproval({
          key: kind === 'Percent' ? 'approval.discount.max_percent' : 'approval.discount.max_amount',
          amount: value,
          context: line ? { visitId: visit.id, lineId: line.id } : { visitId: visit.id },
          summary: t(kind === 'Percent' ? 'bil:discount.approval_percent' : 'bil:discount.approval_amount', { value: faDigits(value.toLocaleString('en-US')) }),
        });
        if (outcome.status === 'cancelled') {
          return;
        }
        if (outcome.status === 'requested') {
          // درخواست ناهمگام: پس از تأیید مدیر همین دیالوگ با همان approval_id اعمال می‌شود.
          setPendingApproval(outcome.approvalId);
          return;
        }
        approvalId = outcome.status === 'approved' ? outcome.approvalId : null;
      }
      update(await send(approvalId));
      onClose();
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <FormDialog
        open
        titleKey={line ? 'bil:discount.line_title' : 'bil:discount.invoice_title'}
        screenId={SCR_STAFF_400}
        onSubmit={() => void submit()}
        onClose={onClose}
        isDirty={valid}
        isSubmitting={busy}
        submitError={error}
        submitKey={pendingApproval ? 'bil:discount.apply_approved' : 'bil:discount.apply'}
      >
        {line ? <Typography variant="body2">{line.title}</Typography> : null}
        <TextField
          select
          label={t('bil:discount.kind')}
          value={kind}
          onChange={(event) => setKind(event.target.value as BilManualDiscountKind)}
          disabled={pendingApproval !== null}
          slotProps={{ htmlInput: { 'data-testid': 'bil-discount-kind' } }}
        >
          <MenuItem value="Percent">{t('bil:discount.Percent')}</MenuItem>
          <MenuItem value="Amount">{t('bil:discount.Amount')}</MenuItem>
        </TextField>
        {kind === 'Percent' ? (
          <TextField
            type="number"
            required
            label={t('bil:discount.percent')}
            value={percent}
            onChange={(event) => setPercent(event.target.value)}
            disabled={pendingApproval !== null}
            slotProps={{ htmlInput: { min: 1, max: 100, 'data-testid': 'bil-discount-percent' } }}
          />
        ) : (
          <div data-testid="bil-discount-amount">
            <MoneyFieldRHF name="amount" control={control} labelKey="bil:discount.amount" required fullWidth disabled={pendingApproval !== null} />
          </div>
        )}
        <TextField
          required
          label={t('bil:discount.reason')}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          slotProps={{ htmlInput: { maxLength: 500, 'data-testid': 'bil-discount-reason' } }}
        />
        {pendingApproval ? (
          <Alert severity="info" data-testid="bil-discount-pending">
            {t('bil:discount.pending')}
          </Alert>
        ) : null}
      </FormDialog>
      {dialog}
    </>
  );
}

/** پرداخت‌کننده مراجعه (US-BIL-003 بخش فاز 1): خود، شخص (مراجعه‌کننده دیگر) یا سازمان؛ بیمه فاز 3. */
export function PayerDialog({ visit, onClose }: { visit: BilVisitView; onClose: () => void }) {
  const { t } = useTranslation('bil');
  const { tenantApi } = useTenantSession();
  const update = useVisitCache(visit.id);
  const [kind, setKind] = useState<BilPayerView['kind']>(visit.payer.kind);
  const [person, setPerson] = useState<PatientSearchItem | null>(null);
  const [organization, setOrganization] = useState(visit.payer.organizationName ?? '');
  const save = useMutation({
    mutationFn: () => {
      const body: BilPayerView =
        kind === 'Self'
          ? { kind, patientId: null, organizationName: null }
          : kind === 'Person'
            ? { kind, patientId: person?.id ?? visit.payer.patientId ?? null, organizationName: null }
            : { kind, patientId: null, organizationName: organization.trim() || null };
      return unwrap(tenantApi.PUT('/api/v1/billing/visits/{id}/payer', { params: { path: { id: visit.id } }, body }));
    },
    onSuccess: (view) => {
      update(view);
      onClose();
    },
  });
  return (
    <FormDialog open titleKey="bil:payer.title" screenId={SCR_STAFF_400} onSubmit={() => save.mutate()} onClose={onClose} isDirty isSubmitting={save.isPending} submitError={save.error}>
      <TextField
        select
        label={t('bil:payer.kind')}
        value={kind}
        onChange={(event) => setKind(event.target.value as BilPayerView['kind'])}
        slotProps={{ htmlInput: { 'data-testid': 'bil-payer-kind' } }}
      >
        {(['Self', 'Person', 'Organization'] as const).map((value) => (
          <MenuItem key={value} value={value}>
            {t(`bil:payer.${value}`)}
          </MenuItem>
        ))}
      </TextField>
      {kind === 'Person' && (
        <PatientPicker labelKey="bil:payer.person" value={person} onChange={setPerson} excludeIds={[visit.patientId]} testId="bil-payer-person" />
      )}
      {kind === 'Organization' && (
        <TextField
          required
          label={t('bil:payer.organization')}
          value={organization}
          onChange={(event) => setOrganization(event.target.value)}
          slotProps={{ htmlInput: { maxLength: 200, 'data-testid': 'bil-payer-organization' } }}
        />
      )}
      <TextField label={t('bil:payer.insurance')} value={t('bil:payer.insurance_phase3')} disabled />
    </FormDialog>
  );
}
